import React from 'react';
import { Box, Paper, Typography, CircularProgress, Alert, alpha } from '@mui/material';
import { DashboardWidget, WidgetDataResult } from '../../../redux/wallboardApi';
import { ScorecardWidget } from './ScorecardWidget';
import { PieChartWidget } from './PieChartWidget';
import { BarGraphWidget } from './BarGraphWidget';
import { LineGraphWidget } from './LineGraphWidget';
import { useDashboardContext, WIDGET_COLORS } from '../pages/DashboardViewPage';

/**
 * Widget Renderer
 * 
 * Renders the appropriate widget component based on widget type.
 * Handles loading states and errors.
 * Supports colorful themes based on widget index.
 */
interface WidgetRendererProps {
  widget: DashboardWidget;
  data?: WidgetDataResult;
  isLoading?: boolean;
  index?: number; // Used for color selection
}

export const WidgetRenderer: React.FC<WidgetRendererProps> = ({
  widget,
  data,
  isLoading,
  index = 0,
}) => {
  // Error state
  if (data?.metadata?.error) {
    return (
      <WidgetWrapper widget={widget} colorIndex={index}>
        <Alert severity="error" sx={{ m: 1 }}>
          {data.metadata.error}
        </Alert>
      </WidgetWrapper>
    );
  }

  // Loading state
  if (isLoading && !data) {
    return (
      <WidgetWrapper widget={widget} colorIndex={index}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
            minHeight: 100,
          }}
        >
          <CircularProgress size={24} sx={{ color: '#fff' }} />
        </Box>
      </WidgetWrapper>
    );
  }

  // Render appropriate widget type
  const renderWidget = () => {
    switch (widget.type) {
      case 'scorecard':
        return <ScorecardWidget widget={widget} data={data} colorIndex={index} />;
      
      case 'piechart':
      case 'donutchart':
        return <PieChartWidget widget={widget} data={data} isDonut={widget.type === 'donutchart'} colorIndex={index} />;
      
      case 'bargraph':
        return <BarGraphWidget widget={widget} data={data} colorIndex={index} />;
      
      case 'linegraph':
      case 'areachart':
        return <LineGraphWidget widget={widget} data={data} isArea={widget.type === 'areachart'} colorIndex={index} />;
      
      case 'table':
        return <TableWidget widget={widget} data={data} />;
      
      case 'gauge':
        return <GaugeWidget widget={widget} data={data} colorIndex={index} />;
      
      default:
        return (
          <Alert severity="warning">
            Unknown widget type: {widget.type}
          </Alert>
        );
    }
  };

  return (
    <WidgetWrapper widget={widget} colorIndex={index}>
      {renderWidget()}
    </WidgetWrapper>
  );
};

/**
 * Widget Wrapper
 * 
 * Common wrapper for all widget types providing consistent styling.
 * Now with colorful gradient themes!
 */
interface WidgetWrapperProps {
  widget: DashboardWidget;
  children: React.ReactNode;
  colorIndex?: number;
}

const WidgetWrapper: React.FC<WidgetWrapperProps> = ({ widget, children, colorIndex = 0 }) => {
  const displayOptions = widget.config?.displayOptions || {};
  const { fontSize } = useDashboardContext();
  
  // Get color theme based on index
  const colorTheme = WIDGET_COLORS[colorIndex % WIDGET_COLORS.length];
  
  // Allow custom colors to override theme
  const useGradient = !displayOptions.backgroundColor;
  const headerBg = displayOptions.backgroundColor || colorTheme.header;
  const textColor = displayOptions.textColor || colorTheme.text;

  return (
    <Paper
      elevation={4}
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        background: useGradient ? colorTheme.gradient : displayOptions.backgroundColor,
        position: 'relative',
        borderRadius: 2.5,
        transition: 'transform 0.18s ease, box-shadow 0.18s ease',
        '&:hover': {
          transform: 'translateY(-3px)',
          boxShadow: '0 12px 32px rgba(0,0,0,0.18)',
        },
      }}
    >
      {/* Subtle top-left accent glow */}
      <Box
        sx={{
          pointerEvents: 'none',
          position: 'absolute',
          top: 0,
          left: 0,
          width: '60%',
          height: '40%',
          background: 'radial-gradient(ellipse at top left, rgba(255,255,255,0.12) 0%, transparent 70%)',
          zIndex: 0,
        }}
      />

      {/* Widget Header */}
      <Box
        sx={{
          px: 2,
          py: 1.25,
          background: headerBg,
          zIndex: 1,
          // Premium left accent stripe
          borderLeft: `3px solid rgba(255,255,255,0.35)`,
        }}
      >
        <Typography
          variant="subtitle2"
          fontWeight={700}
          color={textColor}
          noWrap
          sx={{
            fontSize: `${0.875 * fontSize}rem`,
            letterSpacing: '0.02em',
            textTransform: 'uppercase',
            textShadow: '0 1px 3px rgba(0,0,0,0.35)',
          }}
        >
          {widget.name}
        </Typography>
        {widget.description && (
          <Typography
            variant="caption"
            sx={{
              color: alpha(textColor, 0.85),
              fontSize: `${0.7 * fontSize}rem`,
              letterSpacing: '0.01em',
            }}
            noWrap
          >
            {widget.description}
          </Typography>
        )}
      </Box>

      {/* Widget Content */}
      <Box
        sx={{
          flexGrow: 1,
          p: 1.5,
          overflow: 'auto',
          bgcolor: 'rgba(255,255,255,0.97)',
          borderBottomLeftRadius: 10,
          borderBottomRightRadius: 10,
          zIndex: 1,
        }}
      >
        {children}
      </Box>
    </Paper>
  );
};

/**
 * Table Widget (placeholder implementation)
 */
const TableWidget: React.FC<{ widget: DashboardWidget; data?: WidgetDataResult }> = ({
  widget,
  data,
}) => {
  const { fontSize } = useDashboardContext();
  
  if (!data?.data) {
    return <Typography sx={{ color: 'text.secondary', fontSize: `${0.875 * fontSize}rem` }}>No data available</Typography>;
  }

  // Simple table rendering - can be enhanced with MUI DataGrid
  return (
    <Box sx={{ overflow: 'auto' }}>
      <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: `${0.875 * fontSize}rem` }}>
        Table widget - Data: {JSON.stringify(data.data).slice(0, 100)}...
      </Typography>
    </Box>
  );
};

/**
 * Gauge Widget (placeholder implementation)
 */
const GaugeWidget: React.FC<{ widget: DashboardWidget; data?: WidgetDataResult; colorIndex?: number }> = ({
  widget,
  data,
  colorIndex = 0,
}) => {
  const { fontSize } = useDashboardContext();
  const rawValue = data?.data?.value || 0;
  const value = typeof rawValue === 'number' ? rawValue : Number(rawValue) || 0;
  const max = widget.config?.max || 100;
  const percentage = Math.min(100, (value / max) * 100);
  const colorTheme = WIDGET_COLORS[colorIndex % WIDGET_COLORS.length];
  // Use valueColor for text with better contrast against white background
  const textColor = colorTheme.valueColor || colorTheme.header;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
      }}
    >
      <Box
        sx={{
          position: 'relative',
          display: 'inline-flex',
        }}
      >
        <CircularProgress
          variant="determinate"
          value={percentage}
          size={80 * fontSize}
          thickness={8}
          sx={{
            color: colorTheme.header,
          }}
        />
        <Box
          sx={{
            top: 0,
            left: 0,
            bottom: 0,
            right: 0,
            position: 'absolute',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Typography 
            variant="h6" 
            fontWeight="bold"
            sx={{ 
              color: textColor,
              fontSize: `${1.25 * fontSize}rem`,
            }}
          >
            {Math.round(percentage)}%
          </Typography>
        </Box>
      </Box>
      <Typography 
        variant="body2" 
        sx={{ 
          mt: 1, 
          color: 'text.secondary',
          fontSize: `${0.875 * fontSize}rem`,
        }}
      >
        {value.toLocaleString()} / {max.toLocaleString()}
      </Typography>
    </Box>
  );
};

export default WidgetRenderer;
